import { defineComponent, inject, useSlots, ref, computed, watch, onMounted, onUnmounted, nextTick, createElementBlock, openBlock, Fragment, createVNode, createBlock, normalizeStyle, normalizeClass, createSlots, withCtx, createElementVNode, toDisplayString, unref, renderSlot, Teleport, createCommentVNode, withModifiers, renderList } from "vue";
import { useI18n } from "vue-i18n";
import UInput from "@nuxt/ui/components/Input.vue";
import { Store, Product, Device } from "app-core";
import { useUtilities } from "./index52.js";
const _hoisted_1 = ["onClick", "onMouseover"];
const _hoisted_2 = { class: "min-w-0" };
const _hoisted_3 = {
  key: 0,
  class: "text-body text-muted"
};
const _hoisted_4 = {
  key: 2,
  class: "text-body text-muted"
};
const _hoisted_5 = { class: "shrink-0 text-right" };
const _hoisted_6 = { class: "text-body text-muted" };
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "ProductSearch",
  props: {
    disableQuantity: {
      type: Boolean,
      default: false
    },
    disableWeightedBarcodes: {
      type: Boolean,
      default: false
    },
    disableExactSearch: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    // Attrs don't fall through (multi-root template) — the UInput variant is an
    // explicit prop (e.g. "search" for the P04 sand search bar).
    variant: {
      type: String,
      default: void 0
    },
    /** The UInput's leading icon (the admin list bars' search pill shows i-lucide-search); the "2*" quantity chip replaces it while armed. */
    icon: {
      type: String,
      default: void 0
    },
    dropdownClass: {
      type: String,
      default: ""
    },
    /** The gap between the field and the list below it — 8px, as every other dropdown keeps. */
    dropdownOffsetTop: {
      type: Number,
      default: 8
    },
    enableKeypad: {
      type: Boolean,
      default: false
    },
    /** Old admin SearchProduct behavior: the picked product's name stays in the field. */
    preserveSelection: {
      type: Boolean,
      default: false
    },
    /** The host is a focus-trapping modal (UModal / CodSheet): a row press keeps focus in the field,
     *  and a clicked row clears the field the way Enter does. */
    inModal: {
      type: Boolean,
      default: false
    },
    products: {
      type: Array,
      default: () => void 0
    }
  },
  emits: ["select", "notFound", "blur"],
  setup(__props, { expose: __expose, emit: __emit }) {
    var _a, _b;
    const { t } = useI18n();
    const { debounce } = useUtilities();
    const codConfirm = inject("codConfirm", void 0);
    const barcodeScanner = inject("kontyBarcodeScanner", void 0);
    const emitter = inject("emitter", void 0);
    const slots = useSlots();
    const props = __props;
    const dropdownStyles = ref({});
    const dropdownContainer = ref(null);
    const inputRef = ref(null);
    const inputEl = ref();
    const dropdownItemRefs = ref([]);
    const search = ref("");
    const quantity = ref(0);
    const tenant = Store.get("tenant");
    const shouldShowQuantity = ref(!((_a = tenant == null ? void 0 : tenant.venue) == null ? void 0 : _a.settings.hide_product_quantities) && ((_b = tenant == null ? void 0 : tenant.venue) == null ? void 0 : _b.settings.show_count_in_product_search));
    const dropdownOpen = ref(false);
    const isItemPressing = ref(false);
    const highlightedItemIndex = ref(0);
    const filtered = ref([]);
    const selectedProduct = ref(null);
    const emit = __emit;
    const products = computed(() => props.products || Product.all());
    const barcodeScannerActive = ref(false);
    const quantityChipRef = ref(null);
    const quantityPadding = ref();
    watch(quantity, () => {
      const chip = quantityChipRef.value;
      const field = inputEl.value;
      quantityPadding.value = chip && field ? `${Math.ceil(chip.getBoundingClientRect().right - field.getBoundingClientRect().left) + 8}px` : void 0;
    }, { flush: "post" });
    onMounted(() => {
      var _a2;
      inputEl.value = ((_a2 = inputRef.value) == null ? void 0 : _a2.inputRef) ?? void 0;
      if (props.enableKeypad) {
        emitter == null ? void 0 : emitter.on("keypad", onKeypadHit);
      }
    });
    onUnmounted(() => {
      if (props.enableKeypad) {
        emitter == null ? void 0 : emitter.off("keypad", onKeypadHit);
      }
    });
    watch(dropdownOpen, (val, oldVal) => {
      var _a2, _b2;
      if (!val || oldVal) return;
      const rect = (_b2 = (_a2 = inputRef.value) == null ? void 0 : _a2.$el) == null ? void 0 : _b2.getBoundingClientRect();
      if (!rect) return;
      dropdownStyles.value = {
        top: `${rect.bottom + window.scrollY + props.dropdownOffsetTop}px`,
        left: `${rect.left + window.scrollX}px`,
        width: `${rect.width}px`
      };
    });
    watch(search, (val) => {
      if (props.preserveSelection && selectedProduct.value && selectedProduct.value.name === val) return;
      debounceSearch();
    });
    const debounceSearch = debounce(() => {
      filterProducts();
      highlightedItemIndex.value = 0;
      dropdownOpen.value = filtered.value.length > 0;
    }, 100);
    function onKeypadHit(key) {
      if (key === "." && !search.value) {
        search.value = "0.";
        return;
      }
      if (key !== "Clear" && key !== "Backspace" && key !== "Enter") {
        search.value += key;
        return;
      }
      if (key === "Clear") {
        resetSearch();
        return;
      }
      if (key === "Backspace") {
        search.value = search.value.substring(0, search.value.length - 1);
      }
      handleKeyDown(new KeyboardEvent("keydown", {
        key,
        code: key,
        altKey: false,
        ctrlKey: false,
        shiftKey: false,
        metaKey: false,
        // The handler consumes Enter via preventDefault — keep the synthetic
        // event cancelable so the path matches the real-keystroke contract.
        cancelable: true
      }));
    }
    async function handleKeyDown(event) {
      var _a2;
      if (event.code === "Backspace" && !search.value) {
        quantity.value = 0;
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        if (highlightedItemIndex.value) {
          highlightedItemIndex.value--;
        }
        scroll();
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        if (highlightedItemIndex.value < filtered.value.length - 1) {
          highlightedItemIndex.value++;
        }
        scroll();
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        if (!search.value) return;
        filterProducts();
        const activeProduct = ((_a2 = filtered.value) == null ? void 0 : _a2[highlightedItemIndex.value]) || void 0;
        if (activeProduct) {
          selectProduct(activeProduct);
        } else {
          emit("notFound");
        }
        resetSearch();
      }
    }
    function filterProducts() {
      const searchResult = Product.search(search.value, products.value, {
        limit: 30,
        disableQuantity: props.disableQuantity,
        disableWeightedBarcodes: props.disableWeightedBarcodes,
        disableExactSearch: props.disableExactSearch
      });
      if (searchResult.quantity) {
        quantity.value = searchResult.quantity;
      }
      filtered.value = searchResult.products;
      if (searchResult.query !== void 0) {
        search.value = searchResult.query;
      }
    }
    async function selectProduct(selected, shouldResetSearch = false) {
      var _a2;
      if (props.preserveSelection && ((_a2 = selectedProduct.value) == null ? void 0 : _a2.id) === selected.id) {
        dropdownOpen.value = false;
        search.value = selected.name || "";
        return;
      }
      emit("select", { product: selected, quantity: quantity.value || 1 });
      dropdownOpen.value = false;
      if (props.preserveSelection) {
        selectedProduct.value = selected;
        search.value = selected.name || "";
        quantity.value = 0;
        blur();
        return;
      }
      if (shouldResetSearch) {
        resetSearch();
      }
    }
    async function resetSearch() {
      var _a2;
      if (isItemPressing.value) {
        setTimeout(() => resetSearch(), 100);
        return;
      }
      if (Device.isMobile() && barcodeScannerActive.value) {
        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        await delay(50);
        const isQuantitySearch = quantity.value && !search.value;
        if (isQuantitySearch) return;
      }
      if (props.preserveSelection) {
        search.value = ((_a2 = selectedProduct.value) == null ? void 0 : _a2.name) || "";
        quantity.value = 0;
        dropdownOpen.value = false;
        return;
      }
      search.value = "";
      quantity.value = 0;
      dropdownOpen.value = false;
    }
    function onRowMouseDown(event) {
      isItemPressing.value = true;
      if (props.preserveSelection || props.inModal) event.preventDefault();
    }
    function onFieldBlur() {
      resetSearch();
      setTimeout(() => emit("blur"), 200);
    }
    function setSelected(product) {
      selectedProduct.value = product;
      search.value = (product == null ? void 0 : product.name) || "";
    }
    function clear() {
      if (props.preserveSelection) {
        selectedProduct.value = null;
        search.value = "";
        emit("select", { product: null, quantity: null });
      }
    }
    function focus() {
      var _a2;
      (_a2 = inputEl.value) == null ? void 0 : _a2.focus();
    }
    function isFocused() {
      return document.activeElement === inputEl.value;
    }
    function onBackdropPointerDown(e) {
      var _a2;
      e.stopPropagation();
      const rect = (_a2 = inputEl.value) == null ? void 0 : _a2.getBoundingClientRect();
      if (!rect) return;
      if (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) {
        e.preventDefault();
      }
    }
    function blur() {
      var _a2;
      (_a2 = inputEl.value) == null ? void 0 : _a2.blur();
    }
    function scroll() {
      nextTick(() => {
        const highlightedElement = dropdownItemRefs.value[highlightedItemIndex.value];
        const parentDiv = dropdownContainer.value;
        if (highlightedElement && parentDiv) {
          if (highlightedElement.offsetTop < parentDiv.scrollTop) {
            highlightedElement.scrollIntoView(true);
          } else if (highlightedElement.offsetTop + highlightedElement.clientHeight > parentDiv.scrollTop + parentDiv.clientHeight) {
            highlightedElement.scrollIntoView(false);
          }
        }
      });
    }
    async function scanBarcode() {
      if (!barcodeScanner) return;
      try {
        barcodeScannerActive.value = true;
        const rawValue = await barcodeScanner.scan();
        if (!rawValue) return;
        search.value = rawValue;
        const searchResult = Product.search(search.value, products.value, {
          limit: 1,
          disableQuantity: props.disableQuantity,
          disableWeightedBarcodes: props.disableWeightedBarcodes,
          disableExactSearch: props.disableExactSearch
        });
        if (searchResult.products[0]) {
          if (searchResult.quantity && searchResult.quantity !== 1) {
            quantity.value = searchResult.quantity;
          }
          selectProduct(searchResult.products[0], true);
        } else {
          codConfirm == null ? void 0 : codConfirm({
            title: t("general.error"),
            message: t("retail.order.product_not_found"),
            buttonTrueText: t("general.continue"),
            buttonCancelText: ""
          });
        }
      } catch (error) {
        console.log((error == null ? void 0 : error.message) || error);
      } finally {
        barcodeScannerActive.value = false;
      }
    }
    __expose({ focus, scanBarcode, setSelected, clear });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock(Fragment, null, [
        createVNode(UInput, {
          ref_key: "inputRef",
          ref: inputRef,
          modelValue: search.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => search.value = $event),
          class: normalizeClass(["product-search", [_ctx.$attrs.class, { "product-search--open": dropdownOpen.value }]]),
          variant: props.variant,
          icon: props.icon,
          placeholder: props.placeholder,
          disabled: props.disabled,
          style: normalizeStyle(quantityPadding.value ? { paddingInlineStart: quantityPadding.value } : void 0),
          onFocus: _cache[1] || (_cache[1] = ($event) => search.value = ""),
          onBlur: onFieldBlur,
          onKeydown: handleKeyDown
        }, createSlots({ _: 2 }, [
          quantity.value ? {
            name: "leading",
            fn: withCtx(() => [
              createElementVNode("span", {
                ref_key: "quantityChipRef",
                ref: quantityChipRef,
                class: "qty-chip bg-(--app-tint-accent) text-(--app-text-accent)"
              }, toDisplayString(quantity.value) + " ×", 513)
            ]),
            key: "0"
          } : void 0,
          unref(slots).end ? {
            name: "trailing",
            fn: withCtx(() => [
              renderSlot(_ctx.$slots, "end", {}, void 0, true)
            ]),
            key: "1"
          } : void 0
        ]), 1032, ["modelValue", "class", "variant", "icon", "placeholder", "disabled", "style"]),
        (openBlock(), createBlock(Teleport, { to: "body" }, [
          isFocused() && dropdownOpen.value ? (openBlock(), createElementBlock("div", {
            key: 0,
            class: "product-search-backdrop",
            onPointerdown: onBackdropPointerDown
          }, null, 32)) : createCommentVNode("", true),
          dropdownOpen.value ? (openBlock(), createElementBlock("div", {
            key: 1,
            ref_key: "dropdownContainer",
            ref: dropdownContainer,
            class: normalizeClass(["product-search-dropdown", props.dropdownClass]),
            style: normalizeStyle(dropdownStyles.value),
            onPointerdown: _cache[7] || (_cache[7] = withModifiers(() => {
            }, ["stop"])),
            onFocusin: _cache[8] || (_cache[8] = withModifiers(() => {
            }, ["stop"]))
          }, [
            (openBlock(true), createElementBlock(Fragment, null, renderList(filtered.value, (item, index) => {
              return openBlock(), createElementBlock("button", {
                ref_for: true,
                ref: (el) => {
                  if (el) dropdownItemRefs.value[index] = el;
                },
                key: "prod" + index,
                type: "button",
                class: normalizeClass(["product-search-row", { highlight: highlightedItemIndex.value === index }]),
                onClick: ($event) => selectProduct(item, props.inModal || !isFocused()),
                onMouseover: ($event) => highlightedItemIndex.value = index,
                onMousedown: onRowMouseDown,
                onMouseup: _cache[2] || (_cache[2] = ($event) => {
                  isItemPressing.value = false;
                }),
                onMouseleave: _cache[3] || (_cache[3] = ($event) => {
                  isItemPressing.value = false;
                }),
                onTouchstart: _cache[4] || (_cache[4] = ($event) => isItemPressing.value = true),
                onTouchend: _cache[5] || (_cache[5] = ($event) => {
                  isItemPressing.value = false;
                }),
                onTouchcancel: _cache[6] || (_cache[6] = ($event) => {
                  isItemPressing.value = false;
                })
              }, [
                createElementVNode("div", _hoisted_2, [
                  createElementVNode("strong", null, toDisplayString(item.name), 1),
                  item.code ? (openBlock(), createElementBlock("div", _hoisted_3, toDisplayString(unref(t)("general.code")) + ": " + toDisplayString(item.code), 1)) : createCommentVNode("", true),
                  shouldShowQuantity.value ? (openBlock(), createElementBlock("div", {
                    key: 1,
                    class: normalizeClass(["text-body", item.getCount() < 0 ? "text-error" : "text-muted"])
                  }, toDisplayString(unref(t)("general.quantity")) + ": " + toDisplayString(item.getCount()), 3)) : createCommentVNode("", true),
                  item.barcode ? (openBlock(), createElementBlock("div", _hoisted_4, toDisplayString(unref(t)("general.barcode")) + ": " + toDisplayString(item.barcode), 1)) : createCommentVNode("", true)
                ]),
                createElementVNode("div", _hoisted_5, [
                  createElementVNode("div", null, toDisplayString(item.getPriceAfterTax().toFormat()), 1),
                  createElementVNode("div", _hoisted_6, " (" + toDisplayString(unref(t)("general.pre_tax")) + " " + toDisplayString(item.getPriceBeforeTax().toFormat()) + ") ", 1)
                ])
              ], 42, _hoisted_1);
            }), 128))
          ], 38)) : createCommentVNode("", true)
        ]))
      ], 64);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=index97.js.map
